/**
 * ContentReaderPage - Unified three-panel content reader
 *
 * Mirrors DocumentationReader.tsx layout:
 *  - Left: ContentSidebar (item list, search, filter, create/import)
 *  - Center: Editable title + BlockNote editor with auto-save
 *  - Right: ContentMetadataSidebar (properties, social, comments, analytics)
 *
 * Route: /sm/:shortId/co (with optional /:itemId in URL path)
 */

import { useState, useCallback, useEffect, useLayoutEffect, useRef, useMemo, lazy, Suspense } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, Save, FileText, Plus, Upload, ArrowLeft, SlidersHorizontal, Share2, MessageSquare, BarChart3, Clock, List } from 'lucide-react';
import { PageLayout } from '../../../components/layout/PageLayout';
import { PivotSidebar, type PivotDimension } from '../../../components/shared/PivotSidebar';
import { PropertiesTab, SocialTab, HistoryTab } from '../components/ContentMetadataSidebar';
import { useRightPanelRegistry, type PanelTabRegistration } from '../../../contexts/RightPanelRegistryContext';
import { CommentsSection } from '../../../components/shared/CommentsSection';
import { ItemAnalyticsCard } from '../../../components/shared/ItemAnalyticsCard';
import { DocOutlineContent } from '../../../components/documentation/DocOutline';
import { ContentListCenter } from '../components/ContentListCenter';
import { ReaderCenterShell } from '../../../components/shared/ReaderCenterShell';
import { useProject } from '../../../contexts/ProjectContext';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import {
  contentApi,
  CONTENT_TYPE_LABELS,
  STATUS_COLORS,
  STATUS_LABELS,
  type ContentItem,
  type ContentStatus,
  type ContentType,
  type Author,
  type SocialCopy,
} from '../../../api/content';
import { documentsApi, resolveInitialContent, type EditorDocument } from '../../../api/documents';
import { ImportDialog } from '../../../components/shared/ImportDialog';
import { NotionImportDialog } from '../../../components/shared/NotionImportDialog';
import { LinkedInComposer } from '../components/LinkedInComposer';

// Lazy-load BlockNote editor
const MarthaEditor = lazy(
  () => import('../../../components/editor/MarthaEditor')
);

const METADATA_COLLAPSED_KEY = 'content-metadata-collapsed';

function EditorSkeleton() {
  return (
    <div className="animate-pulse space-y-4 py-4">
      <div className="h-4 bg-neutral-100 rounded w-full" />
      <div className="h-4 bg-neutral-100 rounded w-11/12" />
      <div className="h-4 bg-neutral-50 rounded w-4/5" />
      <div className="h-4 bg-neutral-50 rounded w-full" />
      <div className="h-4 bg-neutral-100 rounded w-3/4" />
    </div>
  );
}

interface ContentReaderPageProps {
  defaultContentType?: ContentType;
  routeSegment?: string;
}

const ContentReaderPage = ({ defaultContentType, routeSegment = 'co' }: ContentReaderPageProps) => {
  const params = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const { currentProject, buildProjectUrl } = useProject();
  const { currentWorkspace } = useWorkspace();
  // Use workspace ID directly — this page is workspace-scoped, not project-scoped.
  // Fallback to currentProject?.workspaceId for legacy/project-scoped contexts.
  const workspaceId = currentWorkspace?.id ?? currentProject?.workspaceId;
  const projectId = currentProject?.id;
  const seg = routeSegment;

  // Pivot dimensions for All Content view
  const ALL_CONTENT_DIMENSIONS: PivotDimension<ContentItem>[] = useMemo(() => [
    {
      id: 'content_type',
      label: 'Content Type',
      getKey: (item) => item.content_type,
      getLabel: (key) => CONTENT_TYPE_LABELS[key] || key,
    },
    {
      id: 'status',
      label: 'Status',
      getKey: (item) => item.status,
      getLabel: (key) => STATUS_LABELS[key] || key,
    },
    {
      id: 'campaign',
      label: 'Campaign',
      getKey: (item) => item.campaign || null,
    },
    {
      id: 'pillar',
      label: 'Pillar',
      getKey: (item) => item.pillar || null,
    },
  ], []);

  // Pivot dimensions for typed routes (type already locked)
  const TYPED_CONTENT_DIMENSIONS: PivotDimension<ContentItem>[] = useMemo(() => [
    {
      id: 'status',
      label: 'Status',
      getKey: (item) => item.status,
      getLabel: (key) => STATUS_LABELS[key] || key,
    },
    {
      id: 'campaign',
      label: 'Campaign',
      getKey: (item) => item.campaign || null,
    },
    {
      id: 'pillar',
      label: 'Pillar',
      getKey: (item) => item.pillar || null,
    },
  ], []);

  // ── Sidebar collapse state ──
  const [metadataCollapsed, setMetadataCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(METADATA_COLLAPSED_KEY) === 'true';
    }
    return false;
  });

  const handleToggleMetadata = useCallback(() => {
    setMetadataCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(METADATA_COLLAPSED_KEY, String(next));
      return next;
    });
  }, []);

  // ── Items list state ──
  const [items, setItems] = useState<ContentItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(true);

  // ── Active item state ──
  const [item, setItem] = useState<ContentItem | null>(null);
  const [itemLoading, setItemLoading] = useState(false);
  const pendingItemIdRef = useRef<string | null>(null);

  // Keep last non-null item so sidebar/center never unmount during navigation
  const lastItemRef = useRef<ContentItem | null>(null);
  if (item) lastItemRef.current = item;
  const displayItem = item || lastItemRef.current;

  // ── Metadata state ──
  const [authors, setAuthors] = useState<Author[]>([]);
  const [socialCopies, setSocialCopies] = useState<SocialCopy[]>([]);
  const [metaSaving, setMetaSaving] = useState(false);

  // ── Document editor state ──
  const [doc, setDoc] = useState<EditorDocument | null>(null);
  const [docLoading, setDocLoading] = useState(false);
  const [docSaveStatus, setDocSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [creatingDoc, setCreatingDoc] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const titleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedContentRef = useRef<string>('');
  const pendingDocIdRef = useRef<string | null>(null);
  const [editedTitle, setEditedTitle] = useState('');

  // ── Import dialogs ──
  const [showImport, setShowImport] = useState(false);
  const [showNotionImport, setShowNotionImport] = useState(false);

  // ── Fetch items list ──
  const fetchItems = useCallback(() => {
    if (!workspaceId) return;
    setItemsLoading(true);
    contentApi
      .listItems(workspaceId, defaultContentType ? { content_type: defaultContentType } : undefined)
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setItemsLoading(false));
  }, [workspaceId, defaultContentType]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Derive itemId inline — fall back to first item (no navigate needed)
  const itemId = params.itemId || items[0]?.id || undefined;

  // Silently update the URL for bookmarkability when we derived the itemId
  useLayoutEffect(() => {
    if (!params.itemId && itemId) {
      navigate(buildProjectUrl(`${seg}/${itemId}`, 'sm'), { replace: true });
    }
  }, [params.itemId, itemId, navigate, buildProjectUrl, seg]);

  // ── Fetch authors (once) ──
  useEffect(() => {
    if (!workspaceId) return;
    contentApi.listAuthors(workspaceId).then(setAuthors).catch(() => {});
  }, [workspaceId]);

  // ── Fetch active item + social copies when itemId changes ──
  // Stale-response guard: only apply results if this itemId is still current
  useEffect(() => {
    if (!workspaceId || !itemId) {
      setItem(null);
      pendingItemIdRef.current = null;
      return;
    }
    pendingItemIdRef.current = itemId;
    // Only show loading skeleton on initial load (nothing to display yet)
    if (!item) setItemLoading(true);
    Promise.all([
      contentApi.getItem(workspaceId, itemId),
      contentApi.getSocialCopies(workspaceId, itemId),
    ])
      .then(([fetchedItem, copies]) => {
        if (pendingItemIdRef.current !== itemId) return; // stale
        setItem(fetchedItem);
        setEditedTitle(fetchedItem.title);
        setSocialCopies(copies);
      })
      .catch(() => {
        if (pendingItemIdRef.current !== itemId) return; // stale
        setItem(null);
      })
      .finally(() => {
        if (pendingItemIdRef.current === itemId) setItemLoading(false);
      });
  }, [workspaceId, itemId]);

  // Reload the active item (used by history tab restore)
  const reloadItem = useCallback(() => {
    if (!workspaceId || !itemId) return;
    contentApi.getItem(workspaceId, itemId).then((fetched) => {
      setItem(fetched);
      setEditedTitle(fetched.title);
    }).catch(() => {});
  }, [workspaceId, itemId]);

  // ── Fetch linked document when item.document_id changes ──
  // Stale-while-revalidate: keep previous doc visible while loading new one
  useEffect(() => {
    const docId = item?.document_id;
    if (!projectId || !docId) {
      setDoc(null);
      setDocLoading(false);
      pendingDocIdRef.current = null;
      return;
    }
    if (doc?.id === docId) return; // already loaded

    pendingDocIdRef.current = docId;
    setDocLoading(true);
    // Keep previous doc visible — don't call setDoc(null)
    documentsApi
      .get(docId, projectId)
      .then((result) => {
        if (pendingDocIdRef.current !== docId) return;
        setDoc(result as EditorDocument);
        lastSavedContentRef.current = resolveInitialContent(result as EditorDocument);
      })
      .catch(() => {
        if (pendingDocIdRef.current !== docId) return;
        setDoc(null);
      })
      .finally(() => {
        if (pendingDocIdRef.current === docId) setDocLoading(false);
      });
  }, [projectId, item?.document_id]);

  // ── Cleanup timeouts ──
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      if (titleTimeoutRef.current) clearTimeout(titleTimeoutRef.current);
    };
  }, []);

  // ── Auto-save editor content (1500ms debounce) ──
  const handleEditorChange = useCallback(
    (content: string) => {
      if (!projectId || !doc) return;
      if (content === lastSavedContentRef.current) return;

      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      setDocSaveStatus('saving');
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          // documentsApi uses projectId (documents are project-scoped)
          await documentsApi.update(doc.id, projectId, {
            content,
            content_format: 'blocknote',
          });
          lastSavedContentRef.current = content;
          setDocSaveStatus('saved');
          setTimeout(() => setDocSaveStatus('idle'), 2000);
        } catch {
          setDocSaveStatus('idle');
        }
      }, 1500);
    },
    [projectId, doc]
  );

  // ── Auto-save title (1000ms debounce) ──
  const handleTitleChange = useCallback(
    (newTitle: string) => {
      setEditedTitle(newTitle);
      if (!workspaceId || !itemId) return;

      if (titleTimeoutRef.current) clearTimeout(titleTimeoutRef.current);
      titleTimeoutRef.current = setTimeout(async () => {
        try {
          await contentApi.updateItem(workspaceId, itemId, { title: newTitle } as Partial<ContentItem>);
          // Update in sidebar list
          setItems((prev) =>
            prev.map((i) => (i.id === itemId ? { ...i, title: newTitle } : i))
          );
        } catch {
          // silent
        }
      }, 1000);
    },
    [workspaceId, itemId]
  );

  // ── Metadata update (immediate) ──
  const handleUpdateItem = useCallback(
    async (data: Partial<ContentItem>) => {
      if (!workspaceId || !itemId) return;
      setMetaSaving(true);
      try {
        const updated = await contentApi.updateItem(workspaceId, itemId, data);
        setItem(updated);
        // Update in sidebar list
        setItems((prev) => prev.map((i) => (i.id === itemId ? updated : i)));
      } catch {
        // silent
      } finally {
        setMetaSaving(false);
      }
    },
    [workspaceId, itemId]
  );

  // ── Social copy save ──
  const handleSaveSocialCopy = useCallback(
    async (authorId: string, platform: string, text: string) => {
      if (!workspaceId || !itemId) return;
      try {
        const updated = await contentApi.upsertSocialCopy(workspaceId, itemId, authorId, platform, text);
        setSocialCopies((prev) => {
          const idx = prev.findIndex((sc) => sc.author_id === authorId && sc.platform === platform);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = updated;
            return next;
          }
          return [...prev, updated];
        });
      } catch {
        // silent
      }
    },
    [workspaceId, itemId]
  );

  // ── Social distribution (publish / schedule) ──
  const handleSocialSchedule = useCallback(
    async (authorIds: string[], scheduledAt?: string) => {
      if (!workspaceId || !itemId) return;
      try {
        await contentApi.distributeSocial(workspaceId, itemId, authorIds, ['linkedin'], scheduledAt);
      } catch {
        // silent
      }
    },
    [workspaceId, itemId]
  );

  const isSocialType = defaultContentType &&
    ['linkedin_post', 'social_post', 'thread'].includes(defaultContentType);

  // ── Create new content item ──
  const handleCreateItem = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const newItem = await contentApi.createItem(workspaceId, {
        title: 'Untitled Content',
        project_id: projectId || undefined,
        ...(defaultContentType ? { content_type: defaultContentType } : {}),
      });
      setItems((prev) => [newItem, ...prev]);
      navigate(buildProjectUrl(`${seg}/${newItem.id}`, 'sm'));
    } catch {
      // silent
    }
  }, [workspaceId, projectId, navigate, buildProjectUrl, defaultContentType, seg]);

  // ── Create linked document ──
  const handleCreateDocument = useCallback(async () => {
    if (!projectId || !itemId || !item) return;
    setCreatingDoc(true);
    try {
      const newDoc = await documentsApi.create(projectId, {
        title: item.title || 'Untitled',
        doc_type: 'content',
        content_format: 'blocknote',
      });
      await contentApi.updateItem(workspaceId!, itemId, {
        document_id: newDoc.id,
      } as Partial<ContentItem>);
      setItem((prev) => (prev ? { ...prev, document_id: newDoc.id } : null));
    } catch {
      // silent
    } finally {
      setCreatingDoc(false);
    }
  }, [projectId, itemId, item]);

  // ── Delete item ──
  const handleDeleteItem = useCallback(async () => {
    if (!workspaceId || !itemId) return;
    if (!window.confirm('Delete this content item?')) return;
    try {
      await contentApi.deleteItem(workspaceId, itemId);
      setItems((prev) => prev.filter((i) => i.id !== itemId));
      // Navigate to next item
      const remaining = items.filter((i) => i.id !== itemId);
      if (remaining.length > 0) {
        navigate(buildProjectUrl(`${seg}/${remaining[0].id}`, 'sm'), { replace: true });
      } else {
        navigate(buildProjectUrl(seg, 'sm'), { replace: true });
      }
    } catch {
      // silent
    }
  }, [workspaceId, itemId, items, navigate, buildProjectUrl, seg]);

  // ── Item select from sidebar ──
  const handleItemSelect = useCallback(
    (id: string) => {
      navigate(buildProjectUrl(`${seg}/${id}`, 'sm'));
    },
    [navigate, buildProjectUrl, seg]
  );

  // ── Determine badge for content type ──
  const getContentTypeBadge = (ct: ContentItem) => {
    const label = CONTENT_TYPE_LABELS[ct.content_type];
    return { label, className: 'bg-neutral-100 text-neutral-600 border-neutral-200' };
  };

  // ── Save status indicator for center header ──
  const saveStatusIndicator = (
    <>
      {docSaveStatus === 'saving' && (
        <span className="flex items-center gap-1.5 text-sm text-neutral-500">
          <Loader2 size={14} className="animate-spin" />
          Saving...
        </span>
      )}
      {docSaveStatus === 'saved' && (
        <span className="flex items-center gap-1.5 text-sm text-neutral-400">
          <Save size={14} />
          Auto-saved
        </span>
      )}
      {docSaveStatus === 'idle' && item && (
        <span className="flex items-center gap-1.5 text-sm text-neutral-400">
          <Save size={14} />
          Auto-saved
        </span>
      )}
    </>
  );

  // ── Sidebar header actions (import + create) ──
  const [showImportMenu, setShowImportMenu] = useState(false);
  const sidebarHeaderActions = (
    <>
      <div className="relative">
        <button
          onClick={() => setShowImportMenu(!showImportMenu)}
          className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors"
          title="Import"
        >
          <Upload size={14} />
        </button>
        {showImportMenu && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setShowImportMenu(false)} />
            <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-lg shadow-lg border border-neutral-200 py-1 z-20">
              <button
                onClick={() => { setShowImportMenu(false); setShowImport(true); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
              >
                <Upload size={14} className="text-neutral-400" />
                Import CSV
              </button>
              <button
                onClick={() => { setShowImportMenu(false); setShowNotionImport(true); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
              >
                <FileText size={14} className="text-neutral-400" />
                Import from Notion
              </button>
            </div>
          </>
        )}
      </div>
      <button
        onClick={handleCreateItem}
        className="p-1.5 rounded-lg bg-coral-500 text-white hover:bg-coral-600 transition-colors"
        title="New content"
      >
        <Plus size={14} />
      </button>
    </>
  );

  // ── Register metadata tabs in the shared right panel (alongside Martha chat) ──
  const { registerTabs, unregisterTabs } = useRightPanelRegistry();

  useEffect(() => {
    if (!displayItem) { unregisterTabs(); return; }

    const tabs: PanelTabRegistration[] = [
      {
        id: 'properties',
        icon: <SlidersHorizontal size={14} />,
        title: 'Properties',
        render: () => (
          <PropertiesTab
            item={displayItem}
            authors={authors}
            workspaceId={workspaceId}
            onUpdateItem={handleUpdateItem}
            onDeleteItem={handleDeleteItem}
            isSaving={metaSaving}
          />
        ),
      },
      {
        id: 'social',
        icon: <Share2 size={14} />,
        title: 'Social',
        render: () => (
          <SocialTab
            item={displayItem}
            authors={authors}
            socialCopies={socialCopies}
            workspaceId={workspaceId}
            projectId={projectId || ''}
            onSaveSocialCopy={handleSaveSocialCopy}
          />
        ),
      },
      {
        id: 'comments',
        icon: <MessageSquare size={14} />,
        title: 'Comments',
        render: () => (
          <div className="p-4">
            <CommentsSection projectId={workspaceId ?? projectId ?? ''} itemId={displayItem.id} />
          </div>
        ),
      },
      {
        id: 'analytics',
        icon: <BarChart3 size={14} />,
        title: 'Analytics',
        render: () => (
          <div className="p-4">
            <ItemAnalyticsCard projectId={projectId || ''} itemId={displayItem.id} />
          </div>
        ),
      },
    ];

    if (doc) {
      tabs.push({
        id: 'toc',
        icon: <List size={14} />,
        title: 'Contents',
        render: () => <DocOutlineContent document={doc} />,
      });
    }

    registerTabs(tabs);
    return () => unregisterTabs();
  }, [displayItem, authors, socialCopies, workspaceId, projectId, doc, metaSaving,
      handleUpdateItem, handleDeleteItem, handleSaveSocialCopy, registerTabs, unregisterTabs]);

  return (
    <PageLayout variant="full-width" contentBackground="#ffffff">
      <div className="h-full flex">
        {/* Left Sidebar — Content List */}
        <div className="flex flex-col">
          {defaultContentType && (
            <div className="px-3 pt-3 pb-0 border-r border-neutral-200 bg-neutral-50">
              <Link
                to={buildProjectUrl('co', 'sm')}
                className="flex items-center gap-1 text-xs text-neutral-400 hover:text-neutral-600 transition-colors"
              >
                <ArrowLeft size={11} /> All Content
              </Link>
            </div>
          )}
          <PivotSidebar<ContentItem>
            title={defaultContentType ? (CONTENT_TYPE_LABELS[defaultContentType] ?? 'Content') : 'All Content'}
            items={items}
            dimensions={defaultContentType ? TYPED_CONTENT_DIMENSIONS : ALL_CONTENT_DIMENSIONS}
            defaultDimension={defaultContentType ? 'status' : 'content_type'}
            activeItemId={itemId}
            isLoading={itemsLoading}
            headerActions={sidebarHeaderActions}
            getItemId={(item) => item.id}
            getItemTitle={(item) => item.title}
            getItemUpdatedAt={(item) => item.updated_at}
            getItemSearchText={(item) => [item.title, ...(item.tags ?? [])].join(' ')}
            onItemSelect={(item) => handleItemSelect(item.id)}
            storageKey={`content-pivot-${defaultContentType ?? 'all'}`}
          />
        </div>

        {/* Center — Editor or List */}
        {!itemId ? (
          <ContentListCenter
            items={items}
            isLoading={itemsLoading}
            onItemClick={handleItemSelect}
            onCreateItem={handleCreateItem}
          />
        ) : (
          <ReaderCenterShell
            headerContent={saveStatusIndicator}
            isLoading={itemLoading && !item}
          >
            {displayItem ? (
              <div className="flex-1 overflow-y-auto">
                <article className={`px-8 py-8 lg:px-12 lg:py-10 ${metadataCollapsed ? 'max-w-5xl' : 'max-w-4xl'}`}>
                  {/* Content type badge */}
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full border mb-4
                      ${getContentTypeBadge(displayItem).className}`}
                  >
                    {getContentTypeBadge(displayItem).label}
                  </span>

                  {/* Status badge */}
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full ml-2 mb-4
                      ${STATUS_COLORS[displayItem.status]}`}
                  >
                    {STATUS_LABELS[displayItem.status]}
                  </span>

                  {displayItem.is_featured && (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full ml-2 mb-4">
                      Featured
                    </span>
                  )}

                  {/* Editable title — compact for social types */}
                  <input
                    type="text"
                    value={editedTitle}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    placeholder={isSocialType ? 'Post label (internal)...' : 'Content title...'}
                    className={`w-full bg-transparent border-0 outline-none focus:ring-0 placeholder:text-neutral-300
                      hover:bg-neutral-50 focus:bg-neutral-50 rounded-lg px-2 -mx-2 py-1 transition-colors
                      ${isSocialType
                        ? 'text-lg font-semibold text-neutral-700 tracking-tight leading-snug mb-2'
                        : 'text-3xl lg:text-4xl font-bold text-neutral-900 tracking-tight leading-tight mb-3'
                      }`}
                    style={isSocialType ? undefined : { fontFamily: "'Source Serif 4', Georgia, serif" }}
                  />

                  {/* Meta line */}
                  <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-500 mb-8 pb-6 border-b border-neutral-100">
                    {displayItem.author_name && (
                      <span className="flex items-center gap-1.5">
                        <span className="w-5 h-5 rounded-full bg-coral-100 flex items-center justify-center text-[10px] font-bold text-coral-600">
                          {displayItem.author_name.charAt(0).toUpperCase()}
                        </span>
                        {displayItem.author_name}
                      </span>
                    )}
                    <span className="text-xs text-neutral-400">
                      Updated {new Date(displayItem.updated_at).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                  </div>

                  {/* Editor — LinkedIn composer for social types, BlockNote for documents */}
                  {isSocialType ? (
                    <LinkedInComposer
                      item={displayItem}
                      authors={authors}
                      socialCopies={socialCopies}
                      workspaceId={workspaceId!}
                      onSaveSocialCopy={handleSaveSocialCopy}
                      onSchedule={handleSocialSchedule}
                    />
                  ) : displayItem.document_id ? (
                    doc ? (
                      <Suspense fallback={<EditorSkeleton />}>
                        <div className="min-h-[500px] -mx-4 relative">
                          <MarthaEditor
                            key={doc.id}
                            initialContent={resolveInitialContent(doc)}
                            contentFormat={doc.content_format}
                            onChange={handleEditorChange}
                            autoSaveDelay={1500}
                            features={{ mermaid: true, stats: true }}
                          />
                          {docLoading && (
                            <div className="absolute inset-0 bg-white/60 flex items-center justify-center rounded-lg">
                              <Loader2 size={20} className="animate-spin text-neutral-300" />
                            </div>
                          )}
                        </div>
                      </Suspense>
                    ) : docLoading ? (
                      <EditorSkeleton />
                    ) : (
                      <div className="p-8 text-center">
                        <p className="text-sm text-red-500">Failed to load document</p>
                        <p className="text-xs text-neutral-400 mt-1">
                          The linked document may have been deleted
                        </p>
                      </div>
                    )
                  ) : (
                    <div className="rounded-xl border border-neutral-200 p-8 text-center bg-neutral-50/50">
                      <div className="w-12 h-12 bg-neutral-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                        <FileText className="text-neutral-400" size={24} />
                      </div>
                      <p className="text-sm text-neutral-600 mb-1">No document linked yet</p>
                      <p className="text-xs text-neutral-400 mb-4">
                        Create a document to start writing content
                      </p>
                      <button
                        onClick={handleCreateDocument}
                        disabled={creatingDoc}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-coral-500 text-white rounded-lg
                          text-sm font-medium hover:bg-coral-600 transition-colors disabled:opacity-50"
                      >
                        {creatingDoc ? (
                          <>
                            <Loader2 size={14} className="animate-spin" /> Creating...
                          </>
                        ) : (
                          <>
                            <Plus size={14} /> Create Document
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </article>
              </div>
            ) : !itemLoading ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-12">
                <FileText size={32} className="text-neutral-300 mb-3" />
                <h2 className="text-lg font-medium text-neutral-700 mb-1">Content item not found</h2>
                <p className="text-sm text-neutral-500 mb-4">
                  This item may have been deleted or you don't have access to it.
                </p>
                <Link
                  to={buildProjectUrl(seg, 'sm')}
                  className="text-sm text-coral-600 hover:text-coral-700 inline-flex items-center gap-1"
                >
                  <ArrowLeft size={14} /> Back to content list
                </Link>
              </div>
            ) : null}
          </ReaderCenterShell>
        )}

        {/* Right panel tabs registered via useRightPanelRegistry above — renders in GlobalChatSidebar */}
      </div>

      {/* Import Dialogs */}
      <ImportDialog
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={fetchItems}
        title="Import from CSV"
        description="Supports Notion CSV export format"
        acceptFormats=".csv"
        onProcess={async (fileContent) => {
          if (!workspaceId) throw new Error('No workspace selected');
          return contentApi.importCsv(workspaceId, fileContent, projectId || undefined);
        }}
      />

      {workspaceId && (
        <NotionImportDialog
          open={showNotionImport}
          onClose={() => setShowNotionImport(false)}
          onImported={fetchItems}
          projectId={workspaceId}
        />
      )}

    </PageLayout>
  );
};

export default ContentReaderPage;
